import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import { Panel } from '@components/Panel/Panel'
import {
    hasElements,
    ParsedEvent,
    riverRecoverPubKey,
    Stream,
    TimelineEvent,
} from '@towns-protocol/sdk'
import {
    bin_equal,
    bin_toHexString,
    publicKeyToAddress,
    recoverPublicKeyFromDelegateSig,
} from '@towns-protocol/utils'
import { Paragraph } from '@ui'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import { useTownsContext } from 'use-towns-client'

export const VerifyEventSignaturePanel = () => {
    return (
        <Panel label="Verify Event Signature" gap="lg">
            <VerifyEventSignatureContent />
        </Panel>
    )
}

const VerifyEventSignatureContent = () => {
    const { casablancaClient } = useTownsContext()
    const [searchParams] = useSearchParams()
    const eventId = searchParams.get('eventId')
    const streamId = searchParams.get('streamId')
    const [isLoading, setIsLoading] = useState(true)
    const [event, setEvent] = useState<ParsedEvent>()
    const [timelineEvent, setTimelineEvent] = useState<TimelineEvent>()
    const [error, setError] = useState<string>()
    const [encryptionSessionInfo, setEncryptionSessionInfo] = useState<string[]>()
    const [recoveredPublicKey, setRecoveredPublicKey] = useState<Uint8Array>()
    const [recoveredPublicKeyFromDelegateSig, setRecoveredPublicKeyFromDelegateSig] =
        useState<Uint8Array>()

    useEffect(() => {
        if (!streamId || !eventId || !casablancaClient) {
            return
        }
        setIsLoading(true)
        setTimeout(() => {
            setIsLoading(false)
            const doIt = async () => {
                let stream: Stream
                try {
                    stream = await casablancaClient.waitForStream(streamId)
                } catch (e) {
                    console.error(e)
                    setError(`Error waiting for stream`)
                    return
                }
                const timelineEvent = stream.view.timeline.find((e) => e.eventId === eventId)
                if (!timelineEvent) {
                    setError('Event not found')
                    return
                }
                setTimelineEvent(timelineEvent)
                const event = await casablancaClient.getPersistedEvent(streamId, eventId)
                setEvent(event)
                if (!event) {
                    setError('Event not found')
                    return
                }
                if (!event.signature) {
                    setError(
                        'Signature not set. Try deleting your persistence store in indexdb and try again.',
                    )
                    return
                }

                if (
                    (event.event.payload.case === 'channelPayload' ||
                        event.event.payload.case === 'dmChannelPayload' ||
                        event.event.payload.case === 'gdmChannelPayload') &&
                    event.event.payload.value.content.case === 'message'
                ) {
                    const encryptedData = event.event.payload.value.content.value
                    const sessionId =
                        encryptedData.sessionId && encryptedData.sessionId.length > 0
                            ? `EncryptedData.sessionId: ${encryptedData.sessionId}`
                            : `EncryptedData.sessionIdBytes: ${bin_toHexString(
                                  encryptedData.sessionIdBytes,
                              )}`
                    setEncryptionSessionInfo([
                        sessionId,
                        `EncryptedData.algorithm ${encryptedData.algorithm}`,
                        `EncryptedData.version ${encryptedData.version}`,
                    ])
                }

                const recoveredPubKeyRaw = riverRecoverPubKey(event.hash, event.signature)
                const recoveredPubKey = publicKeyToAddress(recoveredPubKeyRaw)
                setRecoveredPublicKey(recoveredPubKey)

                if (!hasElements(event.event.delegateSig)) {
                    if (!bin_equal(event.event.creatorAddress, recoveredPubKey)) {
                        setError('recoveredPublicKey does not match creatorAddress')
                    }
                } else {
                    const recoveredPublicKeyFromDelegateSig = recoverPublicKeyFromDelegateSig({
                        delegatePubKey: recoveredPubKeyRaw,
                        delegateSig: event.event.delegateSig,
                        expiryEpochMs: event.event.delegateExpiryEpochMs,
                    })
                    setRecoveredPublicKeyFromDelegateSig(recoveredPublicKeyFromDelegateSig)
                    if (!bin_equal(recoveredPublicKeyFromDelegateSig, event.event.creatorAddress)) {
                        setError('recoveredPublicKeyFromDelegateSig does not match creatorAddress')
                    }
                }
            }
            void doIt()
        }, 33)
    }, [eventId, casablancaClient, streamId])

    if (!casablancaClient) {
        return <Paragraph>Logging in...</Paragraph>
    }

    if (!streamId) {
        return <Paragraph>Invalid streamId Parameter</Paragraph>
    }

    if (!eventId) {
        return <Paragraph>Invalid eventId Parameter</Paragraph>
    }

    return (
        <>
            {isLoading && <ButtonSpinner />}
            {!isLoading && (
                <>
                    <Paragraph color="gray2" size="sm" fontWeight="medium">
                        streamId: {streamId}
                    </Paragraph>
                    <Paragraph color="gray2" size="sm" fontWeight="medium">
                        eventId: {eventId}
                    </Paragraph>
                    {encryptionSessionInfo &&
                        encryptionSessionInfo.map((info, index) => (
                            <Paragraph
                                color="gray2"
                                size="sm"
                                fontWeight="medium"
                                // eslint-disable-next-line react/no-array-index-key
                                key={`esi_${index}`}
                            >
                                {info}
                            </Paragraph>
                        ))}
                    {error && (
                        <Paragraph color="gray2" size="sm" fontWeight="medium">
                            error: {error}
                        </Paragraph>
                    )}
                    {timelineEvent && (
                        <Paragraph color="gray2" size="sm" fontWeight="medium">
                            BlockNumber:{' '}
                            {timelineEvent?.confirmedInBlockNum?.toString() ?? 'unconfirmed'}
                        </Paragraph>
                    )}
                    {timelineEvent && (
                        <Paragraph color="gray2" size="sm" fontWeight="medium">
                            EventNumber:{' '}
                            {timelineEvent.confirmedEventNum?.toString() ?? 'unconfirmed'}
                        </Paragraph>
                    )}
                    {timelineEvent && (
                        <Paragraph color="gray2" size="sm" fontWeight="medium">
                            ConfirmedAtEpochMs: {timelineEvent.confirmedAtEpochMs}
                        </Paragraph>
                    )}
                    {event?.creatorUserId && (
                        <Paragraph color="gray2" size="sm" fontWeight="medium">
                            creatorAddress: {bin_toHexString(event.event.creatorAddress)}
                        </Paragraph>
                    )}
                    {event?.signature && (
                        <Paragraph color="gray2" size="sm" fontWeight="medium">
                            signature: {bin_toHexString(event.signature)}
                        </Paragraph>
                    )}
                    {recoveredPublicKey && (
                        <Paragraph color="gray2" size="sm" fontWeight="medium">
                            recoveredPublicKey: {bin_toHexString(recoveredPublicKey)}
                        </Paragraph>
                    )}
                    {event?.event.delegateSig && (
                        <Paragraph color="gray2" size="sm" fontWeight="medium">
                            delegateSig: {bin_toHexString(event.event.delegateSig)}
                        </Paragraph>
                    )}
                    {recoveredPublicKeyFromDelegateSig && (
                        <Paragraph color="gray2" size="sm" fontWeight="medium">
                            recoveredPublicKeyFromDelegateSig:{' '}
                            {bin_toHexString(recoveredPublicKeyFromDelegateSig)}
                        </Paragraph>
                    )}
                    {!error && (
                        <Paragraph color="gray2" size="sm" fontWeight="medium">
                            Event signature is valid
                        </Paragraph>
                    )}
                </>
            )}
        </>
    )
}
